import { getSearchParams } from "../../../utils/request";
import { api } from "../../api";

const jobsApi = api.injectEndpoints({
  tagTypes: ["Jobs"],
  endpoints: (builder) => ({
    getJobs: builder.query({
      query: ({ page = 1, limit = 20, ...rest }) => {
        const query = getSearchParams({
          legacy: false,
          page,
          limit,
          ...rest,
        });
        return `jobs?${query}`;
      },
      providesTags: (data) =>
        data?.jobs
          ? [
              ...data.jobs.map(({ _id }) => ({ type: "Jobs", id: _id })),
              { type: "Jobs", id: "LIST" },
            ]
          : [{ type: "Jobs", id: "LIST" }],
    }),
    addJob: builder.mutation({
      query(body) {
        return {
          url: "jobs",
          method: "POST",
          body,
        };
      },
      invalidatesTags: [
        { type: "Jobs", id: "LIST" },
        { type: "Schedule", id: "LIST" },
      ],
    }),
    getAdjacentJobs: builder.query({
      query: ({ id, ...rest }) => {
        const query = getSearchParams(rest).toString();
        return query ? `jobs/${id}/adjacent?${query}` : `jobs/${id}/adjacent`;
      },
      transformResponse: (response) => response.data,
      providesTags: [{ type: "Jobs", id: "LIST" }],
    }),
    getJob: builder.query({
      query: ({ jobId, ...rest }) => `jobs/${jobId}?${getSearchParams(rest)}`,
      transformResponse: (response) => response.data,
      providesTags: (result, _error, { jobId }) => {
        const resolvedJobId = result?._id || jobId;
        return [{ type: "Jobs", id: resolvedJobId }];
      },
    }),
    updateJob: builder.mutation({
      query({ id, data }) {
        return {
          url: `/jobs/${id}`,
          method: "PUT",
          body: data,
        };
      },
      /**
       * Saving on Job Show used to feel "slow" because invalidating `{ type: "Jobs", id }`
       * can fan-out into many subscribed queries (schedule/unassigned, legs, job items, etc).
       *
       * Instead, we optimistically patch any cached `getJob`/`getJobs` results that provide
       * the `{ type: "Jobs", id }` tag so the UI updates immediately, then selectively
       * invalidate only the related systems that genuinely need a refetch.
       */
      async onQueryStarted({ id, data }, { dispatch, getState, queryFulfilled }) {
        // Optimistically patch any cached queries that would be invalidated by this job id.
        const patches = [];
        const patchJobObject = (draftJob, patch) => {
          if (!draftJob || !patch || typeof patch !== "object") return;
          Object.assign(draftJob, patch);
        };

        const patchCachesForJob = (patch) => {
          const affected = api.util.selectInvalidatedBy(getState(), [
        { type: "Jobs", id },
          ]);

          affected.forEach(({ endpointName, originalArgs }) => {
            if (endpointName === "getJob") {
              patches.push(
                dispatch(
                  api.util.updateQueryData("getJob", originalArgs, (draft) => {
                    patchJobObject(draft, patch);
                  })
                )
              );
            }

            if (endpointName === "getJobs") {
              patches.push(
                dispatch(
                  api.util.updateQueryData("getJobs", originalArgs, (draft) => {
                    const jobToPatch = draft?.jobs?.find((j) => j?._id === id);
                    patchJobObject(jobToPatch, patch);
                  })
                )
              );
            }
          });
        };

        // Best-effort optimistic update: merge the fields the user just saved.
        patchCachesForJob(data);

        try {
          const fulfilled = await queryFulfilled;
          const updatedJob =
            fulfilled?.data?.data || fulfilled?.data?.job || fulfilled?.data;
          if (updatedJob) {
            // Finalize cache with the server canonical job object.
            patchCachesForJob(updatedJob);
          }
        } catch (e) {
          // Roll back optimistic updates on error.
          patches.forEach((p) => p?.undo?.());
        }
      },
      invalidatesTags: (_result, _error, { id, data }) => {
        // Only invalidate expensive subsystems when the edited fields could affect them.
        const tags = [
          { type: "Jobs", id },
          { type: "Timeline", id: "LIST" },
        ];

        const maybeAffectsSchedule =
          data?.appointmentDate !== undefined ||
          data?.assignedTo !== undefined ||
          data?.status !== undefined ||
          data?.priority !== undefined;

        if (maybeAffectsSchedule) {
          tags.push(
            { type: "Schedule", id: "LIST" },
            { type: "Schedule", id: "UNASSIGNED" },
            { type: "Jobs", id: "UNASSIGNED" }
          );
        }

        return tags;
      },
    }),
    deleteJobs: builder.mutation({
      query({ data }) {
        return {
          url: `/jobs`,
          method: "DELETE",
          body: data,
        };
      },
      invalidatesTags: [{ type: "Jobs", id: "LIST" }],
    }),
    updateGlobalJobRates: builder.mutation({
      query({ id, data }) {
        return {
          url: `/jobs/${id}/rates/global`,
          method: "PUT",
          body: data,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [{ type: "Jobs", id }],
    }),
    updateJobRates: builder.mutation({
      query({ id, data }) {
        return {
          url: `/jobs/${id}/rates`,
          method: "PUT",
          body: data,
        };
      },
      invalidatesTags: (_result, _error, { id }) => [{ type: "Jobs", id }],
    }),
    updateJobAccessories: builder.mutation({
      query({ method, data }) {
        return {
          url: "/jobs/accessory",
          method,
          body: data,
        };
      },
      invalidatesTags: (_result, _error, { data }) => [
        { type: "Jobs", id: data.jobId },
      ],
    }),
    deleteJobAccessory: builder.mutation({
      query({ data }) {
        return {
          url: "/jobs/accessory",
          method: "DELETE",
          body: data,
        };
      },
      invalidatesTags: (_result, _error, { data }) => [
        { type: "Jobs", id: data.jobId },
      ],
    }),
    getJobCountByClient: builder.query({
      query: ({ clientId, ...rest }) =>
        `jobs/${clientId}/client/count?${getSearchParams(rest)}`,
      transformResponse: (response) => response.data,
      providesTags: (_result, _error, data) => {
        return [{ type: "Jobs", id: data.clientId }];
      },
    }),
    setJobRates: builder.mutation({
      query({ id, data }) {
        return {
          url: `/jobs/${id}/rates`,
          method: "PUT",
          body: data,
        };
      },
      invalidatesTags: (_result, _error, arg) => {
        return [{ type: "Jobs", id: arg.id }];
      },
    }),
    getJobMedia: builder.query({
      query: ({ id, ...rest }) => `jobs/${id}/media?${getSearchParams(rest)}`,
      transformResponse: (response) => response.data,
    }),
    sendProposalEmail: builder.mutation({
      query: ({ data, jobId }) => ({
        url: `/jobs/${jobId}/send-proposal`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        { type: "Jobs", id: "LIST" },
        { type: "Timeline", id: "LIST" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetJobsQuery,
  useGetAdjacentJobsQuery,
  useGetJobQuery,
  useDeleteJobsMutation,
  useAddJobMutation,
  useUpdateJobMutation,
  useUpdateGlobalJobRatesMutation,
  useUpdateJobRatesMutation,
  useUpdateJobAccessoriesMutation,
  useDeleteJobAccessoryMutation,
  useGetJobCountByClientQuery,
  useGetJobMediaQuery,
  useLazyGetJobMediaQuery,
  useSetJobRatesMutation,
  useSendProposalEmailMutation,
} = jobsApi;
